<template>
  <div>
    <b-card no-body>
      <div class="card-header" v-if="hasPermission('create schemas')">
        <b-card-title>Project Shape Schemas</b-card-title>
        <b-button
          variant="primary"
          :to="{ name: 'settings-project-shape-schemas-add' }"
        >
          <feather-icon icon="PlusIcon" class="mr-50" />
          <span class="align-middle">Create Schema</span>
        </b-button>
      </div>

      <div class="card-body">
        <b-row class="mb-1">
          <b-col cols="12" md="4">
            <b-form-input
              v-model.trim="search"
              placeholder="Search by schema name"
            />
          </b-col>
        </b-row>

        <div v-if="loading" class="shape-schema-skeleton">
          <div v-for="n in 5" :key="n" class="skeleton-row" />
        </div>

        <b-table
          v-else
          :items="filteredSchemas"
          :fields="fields"
          responsive
          show-empty
          empty-text="No shape schemas found"
        >
          <template #cell(is_active)="data">
            <b-badge :variant="data.item.is_active ? 'success' : 'secondary'">
              {{ data.item.is_active ? 'Active' : 'Inactive' }}
            </b-badge>
          </template>

          <template #cell(attributes_count)="data">
            {{ Array.isArray(data.item.attributes) ? data.item.attributes.length : 0 }}
          </template>

          <template #cell(updated_at)="data">
            {{ formatDate(data.item.updated_at) }}
          </template>

          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="outline-primary"
              :to="{ name: 'settings-project-shape-schemas-edit', params: { id: data.item.id } }"
            >
              Edit
            </b-button>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BButton,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BBadge,
} from 'bootstrap-vue'
import projectShapeSchemasApi from '@/api/settings/projectShapeSchemas'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ProjectShapeSchemasList',
  components: {
    BCard,
    BCardTitle,
    BButton,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BBadge,
  },
  data() {
    return {
      loading: false,
      search: '',
      schemas: [],
      fields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'is_active', label: 'Active', sortable: true },
        { key: 'attributes_count', label: 'Attributes Count', sortable: false },
        { key: 'updated_at', label: 'Updated At', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  computed: {
    filteredSchemas() {
      const term = this.search.toLowerCase()
      if (!term) return this.schemas
      return this.schemas.filter(schema => String(schema.name || '').toLowerCase().includes(term))
    },
  },
  mounted() {
    this.fetchSchemas()
  },
  methods: {
    formatDate(date) {
      if (!date) return '-'
      try {
        return new Date(date).toLocaleString()
      } catch (e) {
        return date
      }
    },
    showError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('common.error'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
          text: message,
        },
      })
    },
    async fetchSchemas() {
      this.loading = true
      try {
        const response = await projectShapeSchemasApi.list()
        this.schemas = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : [])
      } catch (error) {
        this.showError(error?.response?.data?.message || error.message || 'Failed to load shape schemas')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.shape-schema-skeleton .skeleton-row {
  height: 38px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f2f2f2 25%, #e7e7e7 37%, #f2f2f2 63%);
  background-size: 400% 100%;
  animation: shimmer 1.2s ease infinite;
  margin-bottom: 0.5rem;
}

@keyframes shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0 0;
  }
}
</style>
